<template>
  <div>
    <b-modal
      id="edit-insurance-modal"
      v-model="isModalActive"
      title="Edit Insurance"
      @show="onModalShow"
      @hide="resetInsuranceForm"
      hide-footer>
      <validation-observer ref="refEditInsuranceForm">
        <b-row>
          <!-- Copay -->
          <b-col md="6">
            <validation-provider
              #default="validationContext"
              name="Copay"
              rules="required|min_value:0|numeric">
              <b-form-group :label="$t('Copay')" label-for="edit_copay">
                <b-form-input
                  id="edit_copay"
                  v-model="editInsuranceData.copay"
                  type="number"
                  :state="getValidationState(validationContext)"
                  trim
                  placeholder="Copay"
                  min="0" />
                <b-form-invalid-feedback>{{
                  validationContext.errors[0]
                }}</b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
          </b-col>

        </b-row>

        <b-row>
          <!-- Co Insurance -->
          <b-col md="6">
            <validation-provider
              #default="validationContext"
              name="Co Insurance"
              rules="required|min_value:0|numeric">
              <b-form-group :label="$t('CoInsurance')" label-for="edit_co_insurance">
                <b-form-input
                  id="edit_co_insurance"
                  v-model="editInsuranceData.co_insurance"
                  type="number"
                  :state="getValidationState(validationContext)"
                  trim
                  placeholder="Co Insurance"
                  min="0" />
                <b-form-invalid-feedback>{{
                  validationContext.errors[0]
                }}</b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
          </b-col>
        </b-row>

        <b-row>
          <!-- Deducible -->
          <b-col md="6">
            <validation-provider
              #default="validationContext"
              name="Deducible"
              rules="required|min_value:0|numeric">
              <b-form-group :label="$t('Deducible')" label-for="edit_deducible">
                <b-form-input
                  id="edit_deducible"
                  v-model="editInsuranceData.deducible"
                  type="number"
                  :state="getValidationState(validationContext)"
                  trim
                  placeholder="Deducible"
                  min="0" />
                <b-form-invalid-feedback>{{
                  validationContext.errors[0]
                }}</b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
          </b-col>

          <!-- Out Pocket -->
          <b-col md="6">
            <validation-provider
              #default="validationContext"
              name="Out Pocket"
              rules="required|min_value:0|numeric">
              <b-form-group :label="$t('OutPocket')" label-for="edit_out_pocket">
                <b-form-input
                  id="edit_out_pocket"
                  v-model="editInsuranceData.out_pocket"
                  type="number"
                  :state="getValidationState(validationContext)"
                  trim
                  placeholder="Out Pocket"
                  min="0" />
                <b-form-invalid-feedback>{{
                  validationContext.errors[0]
                }}</b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
          </b-col>
        </b-row>

        <div class="d-flex justify-content-end mt-3">
          <b-button
            variant="outline-secondary"
            class="ml-2"
            @click="isModalActive = false"
            :disabled="loading">
            Cancel
          </b-button>
          <b-button
            variant="primary"
            @click="validateAndUpdateInsurance"
            :disabled="loading">
            <span v-if="!loading">Save</span>
            <span v-else>
              <SpinnerLoading />
            </span>
          </b-button>
        </div>
      </validation-observer>
    </b-modal>
    <ToastNotification ref="toast" />
  </div>
</template>

<script>
import { BModal, BFormGroup, BFormInput, BFormInvalidFeedback, BButton, BRow, BCol } from 'bootstrap-vue'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import ToastNotification from '@/components/ToastNotification'
import SpinnerLoading from '@/components/SpinnerLoading'
import axiosP from '@/core/services/apiInsurance/patient'

export default {
  components: {
    BModal,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BButton,
    BRow,
    BCol,
    ValidationObserver,
    ValidationProvider,
    ToastNotification,
    SpinnerLoading,
  },
  props: {
    isEditInsuranceModalActive: {
      type: Boolean,
      required: true,
    },
    selectedAppointment: {
      type: Object,
      default: null,
    },
  },
  model: {
    prop: 'isEditInsuranceModalActive',
    event: 'update:isEditInsuranceModalActive',
  },
  data() {
    return {
      editInsuranceData: {
        copay: 0,
        co_insurance: 0,
        deducible: 0,
        out_pocket: 0,
      },
      loading: false,
      refEditInsuranceForm: null,
      localModalActive: false,
    }
  },
  computed: {
    isModalActive: {
      get() {
        return this.isEditInsuranceModalActive
      },
      set(value) {
        this.$emit('update:isEditInsuranceModalActive', value)
      }
    }
  },
  methods: {
    getValidationState({ dirty, validated, valid = null }) {
      return dirty || validated ? valid : null
    },
    onModalShow() {
      this.loadAppointmentInsuranceData()
    },
    async loadAppointmentInsuranceData() {
      // Verificar si tenemos los datos necesarios
      if (!this.selectedAppointment) {
        this.editInsuranceData = {
          copay: 0,
          co_insurance: 0,
          deducible: 0,
          out_pocket: 0,
        }
        return
      }
      
      // Obtener appointment_id del appointment
      let appointmentId = this.selectedAppointment.id
      
      if (!appointmentId) {
        this.editInsuranceData = {
          copay: 0,
          co_insurance: 0,
          deducible: 0,
          out_pocket: 0,
        }
        return
      }
      
      this.loading = true
      try {
        // Siempre hacer el llamado GET al endpoint de insurance-appoinments
        const response = await axiosP.getPatientInsuranceInfo(appointmentId)
        
        if (response && response.status === 'OK' && response.data) {
          const data = response.data
          
          // Cargar los datos del appointment, type_co_insurance siempre será 0
          const insuranceData = {
            copay: data.copay || 0,
            co_insurance: data.co_insurance || 0,
            deducible: data.deducible || 0,
            out_pocket: data.out_pocket || 0,
          }
          
          this.editInsuranceData = insuranceData
        } else {
          // Mostrar mensaje específico según el tipo de error
          if (response?.status === 'ERROR' && response?.message === 'Appointment not found') {
            this.$refs.toast.warning('Appointment not found. Using default values.')
          } else {
            this.$refs.toast.warning('Error loading insurance data. Using default values.')
          }
          
          this.editInsuranceData = {
            copay: 0,
            co_insurance: 0,
            deducible: 0,
            out_pocket: 0,
          }
        }
      } catch (error) {
        this.$refs.toast.error('Error loading insurance information')
        // Usar valores por defecto en caso de error
        this.editInsuranceData = {
          copay: 0,
          co_insurance: 0,
          deducible: 0,
          out_pocket: 0,
        }
      } finally {
        this.loading = false
      }
    },
    resetInsuranceForm() {
      this.editInsuranceData = {
        copay: 0,
        co_insurance: 0,
        deducible: 0,
        out_pocket: 0,
      }
      this.isModalActive = false
    },
    validateAndUpdateInsurance() {
      this.$refs.refEditInsuranceForm.validate().then(valid => {
        if (valid) {
          this.updateInsurance()
        }
      })
    },
    async updateInsurance() {
      if (!this.selectedAppointment) return

      // Obtener appointment_id del appointment
      let appointmentId = this.selectedAppointment.id
      
      if (!appointmentId) {
        return
      }

      // Asegurar que siempre se envíen valores (0 si no hay valor) y type_co_insurance siempre 0
      const dataToSend = {
        copay: this.editInsuranceData.copay || 0,
        type_co_insurance: 0, // Siempre 0, no editable
        co_insurance: this.editInsuranceData.co_insurance || 0,
        deducible: this.editInsuranceData.deducible || 0,
        out_pocket: this.editInsuranceData.out_pocket || 0,
      }

      this.loading = true
      try {
        // Siempre usar el endpoint de insurance-appoinments
        await axiosP.updatePatientInsuranceInfo(appointmentId, dataToSend)
        
        this.$refs.toast.success('Insurance information updated successfully')
        this.$emit('insurance-updated')
        this.isModalActive = false
        this.resetInsuranceForm()
      } catch (error) {
        this.$refs.toast.error('Error updating insurance information')
      } finally {
        this.loading = false
      }
    },
  },
  watch: {
    isEditInsuranceModalActive(newVal) {
      this.localModalActive = newVal
    },
  },
}
</script>

<style scoped>
.modal-body {
  padding: 1.5rem;
}
</style>
